import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAthleteStore } from '@/store';
import { useAuthStore } from '@/store/authStore';
import { ChevronLeft, LogOut, Camera, Save, AlertCircle } from 'lucide-react';
import { db } from '@/lib/db/db';
import { supabase } from '@/lib/supabase';
import { checkImageSafety } from '@/services/ai/GuardAI';
import type { ExperienceLevel } from '@/types';

export function AccountPage() {
    const navigate = useNavigate();
    const { account, profile, hydrate, logout } = useAuthStore();

    // Legacy fallback for UI (Avatar rendering needs athlete for now until profile avatars are fully hooked up everywhere)
    const { athletes, activeId } = useAthleteStore();
    const athlete = athletes.find(a => a.id === activeId);

    const [name, setName] = useState(profile?.name || athlete?.name || '');
    const [level, setLevel] = useState(profile?.level || athlete?.level || 'Beginner');
    const [team, setTeam] = useState(profile?.team || 'Palaestra Wrestling');
    const [isSaving, setIsSaving] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState(profile?.avatar || athlete?.avatar || '');

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !account) return;

        // 1. Local Size Check
        if (file.size > 2 * 1024 * 1024) {
            alert('Image must be under 2MB.');
            return;
        }

        setIsSaving(true);
        try {
            // 2. AI Safety Check (Nudity/Violence Detection)
            console.log('Profile Upload: Starting Safety Check...');
            const safety = await checkImageSafety(file);
            console.log('Profile Upload: Safety Check Result:', safety);

            if (!safety.safe) {
                alert(`UPLOAD BLOCKED: Our AI detected inappropriate content. Reason: ${safety.reason || 'Safety Violation'}. Continuous violations will result in a permanent ban.`);
                setIsSaving(false);
                return;
            }

            const fileExt = file.name.split('.').pop();
            const supabaseId = profile?.id || (await supabase.auth.getUser()).data.user?.id;
            console.log('Profile Upload: Using Supabase ID:', supabaseId);

            if (!supabaseId) throw new Error('No authenticated user found');

            const fileName = `${supabaseId}-${Math.random()}.${fileExt}`;
            const filePath = `avatars/${fileName}`;

            // 2. Upload to Supabase Storage
            console.log('Profile Upload: Uploading to Storage...', filePath);
            const { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(filePath, file, { upsert: true });

            if (uploadError) {
                console.error('Profile Upload: Storage Error:', uploadError);
                throw uploadError;
            }

            // 3. Get Public URL
            console.log('Profile Upload: Getting Public URL...');
            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(filePath);

            console.log('Profile Upload: Public URL:', publicUrl);
            setAvatarUrl(publicUrl);

            // 4. Update the Profile immediately
            console.log('Profile Upload: Updating database profile...');
            const { error: updateError } = await supabase
                .from('profiles')
                .update({ avatar: publicUrl })
                .eq('id', supabaseId);

            if (updateError) {
                console.error('Profile Upload: Database Update Error:', updateError);
                throw updateError;
            }

            console.log('Profile Upload: Syncing local state (hydrate)...');
            await hydrate();
            console.log('Profile Upload: Success!');
            alert('Profile picture updated!');
        } catch (err: any) {
            console.error('Upload process failed at step:', err);
            alert(`Upload failed: ${err.message || 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!account) return;
        setIsSaving(true);
        try {
            const supabaseId = profile?.id || (await supabase.auth.getUser()).data.user?.id;

            // Update Supabase
            const { error } = await supabase
                .from('profiles')
                .update({ name, level, team, avatar: avatarUrl })
                .eq('id', supabaseId);

            if (error) throw error;

            // Optional: Update legacy local DB 
            if (athlete) {
                await db.athletes.update(athlete.id, { name, level: level as ExperienceLevel, avatar: avatarUrl });
            }

            await hydrate();
            navigate('/profile');
        } catch (err) {
            console.error('Failed to update profile:', err);
            alert('Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    if (!account) {
        return (
            <div className="min-h-screen bg-bg flex items-center justify-center text-text-muted">
                <p>Please sign in to edit your profile.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg pb-32 animate-fade-in text-text-primary p-4 md:p-6">
            <div className="max-w-md mx-auto space-y-6">

                {/* Header Section */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-text-muted hover:text-white transition-colors"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-2xl font-bold text-white">Account Settings</h1>
                </div>

                {/* Safety Warning Banner */}
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 text-red-400 font-bold text-[10px] uppercase tracking-widest mb-1">
                        <AlertCircle size={14} />
                        Strict Content Policy
                    </div>
                    <p className="text-[10px] text-red-200/70 leading-relaxed">
                        Inappropriate uploads (sexual content, violence, or hate speech) are strictly prohibited.
                        Violations will result in a permanent **account ban**, **IP blocking**, and reporting to the **National Center for Missing & Exploited Children (NCMEC)** and local law enforcement.
                    </p>
                </div>

                <div className="bg-bg-surface border border-white/5 rounded-[20px] p-6 text-center">
                    <div className="w-24 h-24 mx-auto bg-gradient-to-tr from-gold-500 to-bg-surface2 rounded-full p-[2px] mb-4 relative group">
                        <div className="w-full h-full rounded-full border-[3px] border-bg bg-bg-elevated flex items-center justify-center text-3xl overflow-hidden relative">
                            {avatarUrl && (avatarUrl.startsWith('http') || avatarUrl.startsWith('data:')) ? (
                                <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-3xl">{avatarUrl || profile?.name?.[0] || name?.[0] || 'U'}</span>
                            )}

                            {isSaving && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                    <div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}
                        </div>

                        <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white border-2 border-bg hover:scale-110 transition-transform cursor-pointer shadow-lg shadow-blue-500/20">
                            <Camera size={14} />
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileUpload}
                                disabled={isSaving}
                            />
                        </label>
                    </div>
                    <p className="text-xs font-bold text-text-muted uppercase tracking-widest">{account.role}</p>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-text-muted uppercase tracking-wider ml-1">Email (Read Only)</label>
                        <input
                            type="email"
                            value={account.email}
                            disabled
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-text-muted cursor-not-allowed"
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">Display Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-white/20 focus:border-gold-500/50 focus:outline-none transition-colors"
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">Team / Club</label>
                        <input
                            type="text"
                            value={team}
                            onChange={(e) => setTeam(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-white/20 focus:border-gold-500/50 focus:outline-none transition-colors"
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-gold-500 uppercase tracking-wider ml-1">Experience Level</label>
                        <select
                            value={level}
                            onChange={(e) => setLevel(e.target.value as any)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white appearance-none focus:border-gold-500/50 focus:outline-none transition-colors"
                        >
                            <option value="Beginner">Beginner</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Advanced">Advanced</option>
                            <option value="Elite">Elite</option>
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={isSaving}
                        className="w-full py-4 rounded-xl bg-gold-500 text-bg-900 font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-gold-400 transition-colors disabled:opacity-50 mt-6"
                    >
                        <Save size={18} />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </form>

                <div className="pt-8">
                    <button
                        onClick={handleLogout}
                        className="w-full py-4 rounded-xl border border-red-500/30 text-red-400 font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-red-500/10 transition-colors"
                    >
                        <LogOut size={18} />
                        Logout Options
                    </button>
                    <p className="text-center text-[10px] text-text-muted mt-3">Account updates apply to all devices.</p>
                </div>

            </div>
        </div>
    );
}
